import { useId } from 'react';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import type { ProBuilderConsentMode } from '~/hooks/queries/use-pro-builder';
import { cn } from '~/lib/utils';
import { PRO_BUILDER_CONSENT_MODE_OPTIONS } from './pro-builder-copy';

/**
 * FRONT-3230 — the three "how much to ask" options as one radio group, the
 * same control on the consent card ("Next time") and in the header's gear
 * menu. Pure: the value (undefined = nothing to show selected yet, e.g. the
 * saved setting is still loading) goes in, the pick comes out.
 */
export function ProBuilderConsentModeChoice({
  heading,
  value,
  onChange,
  disabled = false,
  className,
  testId,
}: {
  heading: string;
  value: ProBuilderConsentMode | undefined;
  onChange: (mode: ProBuilderConsentMode) => void;
  disabled?: boolean;
  className?: string;
  testId: string;
}) {
  const id = useId();
  const headingId = `${id}-heading`;
  return (
    <div data-testid={testId} className={cn('flex flex-col gap-2', className)}>
      <p id={headingId} className="text-[length:var(--text-xs)] font-medium text-muted-foreground">
        {heading}
      </p>
      <RadioGroup
        aria-labelledby={headingId}
        value={value ?? ''}
        onValueChange={(v) => {
          const picked = PRO_BUILDER_CONSENT_MODE_OPTIONS.find((o) => o.value === v);
          if (picked) onChange(picked.value);
        }}
        disabled={disabled}
        className="gap-1.5"
      >
        {PRO_BUILDER_CONSENT_MODE_OPTIONS.map((option) => {
          const itemId = `${id}-${option.value}`;
          return (
            <label
              key={option.value}
              htmlFor={itemId}
              className={cn(
                'flex items-center gap-2.5 text-[length:var(--text-sm)] leading-snug text-foreground',
                disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
              )}
            >
              <RadioGroupItem id={itemId} value={option.value} data-testid={`${testId}-${option.value}`} />
              <span>{option.label}</span>
            </label>
          );
        })}
      </RadioGroup>
    </div>
  );
}
